import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Inject, SecurityContext, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { interval } from 'rxjs';
import { take, takeLast, tap } from 'rxjs/operators';
import { MmuAnalyticsService } from '../../analytics/mmu-analytics.service';
import { FeatureFlagsMap, MmuFeatureFlagsService } from '../../feature-flags';
import { MmuCookiePreferences } from '../mmu-cookie-preferences';


@Component({
  selector: 'mmu-ff-ent-gtm-tracking-cookie-preferences-dialog',
  templateUrl: './mmu-cookie-preferences-dialog.component.html',
  styleUrls: ['./mmu-cookie-preferences-dialog.component.scss']
})
export class MmuFFEntGTMTrackingCookiePreferencesDialog implements AfterViewInit {
  @ViewChild('allowAllButton', { static: true, read: ElementRef })
  private _allowAllBtn!: ElementRef;

  get safeMoreInfoAnchor(): string | null {
    return this.sanitizer.sanitize(
      SecurityContext.HTML,
      `<a href="/user/legal/data-privacy" target="_blank">${this.translateService.instant('cookieSettings.dialog.moreInformation')}</a>`
    );
  }

  readonly settingsChanged = new EventEmitter<MmuCookiePreferences>();

  settings: MmuCookiePreferences = {
    required: true,
    statistics: false
  };
  formDisabled = false;

  constructor(
    private translateService: TranslateService,
    private modal: NgbActiveModal,
    private sanitizer: DomSanitizer,
    // use forwardRef here to resolve circular dependency between MmuAnalyticsService and MmuCookiePreferencesService - both depend on each other
    @Inject(forwardRef(() => MmuAnalyticsService)) private analyticsService: MmuAnalyticsService,
    private featureFlagsService: MmuFeatureFlagsService
  ) {
  }

  ngAfterViewInit(): void {
    // Focus allow all cookies button
    this._allowAllBtn.nativeElement.focus({ preventScroll: true });
  }

  /**
   * Accepts the form
   *
   * @param form form control
   * @param sendEvent whether to send the settingsChanged or not
   */
  accept(form: NgForm, sendEvent = true): void {
    if (!form.invalid) {
      this.trackCookieBannerInteraction();

      if (sendEvent) {
        this.settingsChanged.emit(this.settings);
      }

      this.modal.close();
    }
  }

  /**
   * Animate and select all checkboxes and close dialog afterwards
   *
   * @param form The form control
   */
  selectAll(form: NgForm): void {
    this.formDisabled = true;

    // Send the change event before the animation completes to prevent issues in the e2e tests which are sometimes quicker than the animation
    this.settingsChanged.emit({
      required: true,
      statistics: true
    });

    interval(250).pipe(
      take(Object.keys(form.value).length + 1),
      tap((val) => {
        if (val === 0) {
          this.settings.statistics = true;
        }
      }),
      takeLast(1)
      // Do not send the change event again, as it was already sent before the animation started
    ).subscribe(() => this.accept(form, false));
  }


  private trackCookieBannerInteraction(): void {
    /**
     * @deprecated FeatureFlagsMap.FFEntGTMTracking
     */
    if (this.featureFlagsService.isFeatureEnabled(FeatureFlagsMap.FFEntGTMTracking)) {
      this.analyticsService.emitCustomEvent('MieleBasic_ConsentInteraction', this.settings.statistics ? 'acceptAll' : 'acceptRequired');
    }
  }
}
